import type { QueryResult } from "@powersync/common"
import * as Sentry from "@sentry/react"
import type { UploadMonitor } from "./uploadMonitor"
import {
  FAILURES_BY_PROJECT_SQL,
  ITEMS_BY_PROJECT_SQL,
  PHOTOS_BY_PROJECT_SQL,
  QUEUE_BY_PROJECT_SQL,
  type FailuresByProjectRow,
  type ItemsByProjectRow,
  type PhotosByProjectRow,
  type QueueByProjectRow,
} from "./uploadQueries"
import {
  getUploadSignals,
  reportPhotoQueueEmpty,
  reportRowQueueEmpty,
  subscribeToUploadSignals,
} from "./uploadSignals"

/** The slice of the PowerSync database the watches need. */
export type WatchableDatabase = {
  watch: (
    sql: string,
    parameters: unknown[],
    handler: {
      onResult: (result: QueryResult) => void
      onError?: (error: Error) => void
    },
    options: { signal: AbortSignal; tables: string[]; throttleMs: number },
  ) => void
  getAll: (sql: string, parameters: unknown[]) => Promise<unknown[]>
}

/**
 * How often the aggregates re-run at most. Uploads complete a batch at a
 * time, so this mostly coalesces the writes of one batch.
 */
const WATCH_THROTTLE_MS = 500

function rowsOf<T>(result: QueryResult): T[] {
  return (result.array ?? result.rows?._array ?? []) as T[]
}

const toNumber = (value: unknown) => Number(value ?? 0)

function toFailureGroups(rows: FailuresByProjectRow[]) {
  return rows.map((row) => ({
    projectId: row.project_id,
    failures: toNumber(row.failures),
  }))
}

/**
 * Report, once each time it starts, a stalled row queue holding nothing but
 * deleted records' changes. The upload status hides those (see
 * QUEUED_OPS_CTE), so this stall is the one the user has no way of seeing.
 */
function reportHiddenDeletionStalls(monitor: UploadMonitor): () => void {
  let reported = false
  return monitor.subscribe(() => {
    const status = monitor.getGlobalSnapshot()
    if (!status.stalledOnDeletion) {
      reported = false
      return
    }
    if (reported) return
    reported = true
    const { rowsFailingSince } = getUploadSignals()
    Sentry.captureMessage(
      "[PowerSync] Upload queue stalled on deleted records' changes",
      {
        level: "warning",
        tags: {
          component: "powersync_upload",
          app_version:
            typeof __BUILD_ID__ === "undefined" ? "unknown" : __BUILD_ID__,
          failure_policy: "stalled_hidden_deletion",
        },
        contexts: {
          upload_status: {
            deletion_ops: status.deletions,
            photos: status.photos,
            failures: status.failures,
            stalled_ms:
              rowsFailingSince === undefined
                ? null
                : Date.now() - rowsFailingSince,
          },
        },
      },
    )
  })
}

/**
 * Feed the monitor from the database, the connector's signals and the
 * browser's online state. Returns a function that stops everything.
 *
 * Each watch names the one table whose changes matter (`tables`), so the
 * lookups into observations, link tables and so on don't re-run the
 * aggregate every time sync downloads a teammate's records.
 */
export function startUploadMonitor(
  db: WatchableDatabase,
  monitor: UploadMonitor,
): () => void {
  const controller = new AbortController()
  const { signal } = controller
  const onError = (label: string) => (error: Error) => {
    console.error(`[UploadMonitor] ${label} watch failed:`, error)
  }

  db.watch(
    QUEUE_BY_PROJECT_SQL,
    [],
    {
      onResult: (result) => {
        const groups = rowsOf<QueueByProjectRow>(result).map((row) => ({
          projectId: row.project_id,
          rows: toNumber(row.row_ops),
          tracks: toNumber(row.track_ops),
          deletions: toNumber(row.deletion_ops),
          firstClientId: toNumber(row.first_client_id),
          ahead: toNumber(row.ahead),
        }))
        // Whatever was failing or waiting has gone with the queue; don't let
        // it colour the next upload.
        if (
          groups.every(
            (group) => group.rows + group.tracks + group.deletions === 0,
          )
        ) {
          reportRowQueueEmpty()
        }
        monitor.setQueue(groups)
      },
      onError: onError("ps_crud"),
    },
    { signal, tables: ["ps_crud"], throttleMs: WATCH_THROTTLE_MS },
  )

  db.watch(
    PHOTOS_BY_PROJECT_SQL,
    [],
    {
      onResult: (result) => {
        const groups = rowsOf<PhotosByProjectRow>(result).map((row) => ({
          projectId: row.project_id,
          photos: toNumber(row.photos),
          bytes: toNumber(row.bytes),
        }))
        if (groups.every((group) => group.photos === 0)) {
          reportPhotoQueueEmpty()
        }
        monitor.setPhotos(groups)
      },
      onError: onError("attachments"),
    },
    {
      signal,
      tables: ["attachments", "attachment_failures"],
      throttleMs: WATCH_THROTTLE_MS,
    },
  )

  db.watch(
    ITEMS_BY_PROJECT_SQL,
    [],
    {
      onResult: (result) =>
        monitor.setItems(
          rowsOf<ItemsByProjectRow>(result).map((row) => ({
            projectId: row.project_id,
            items: toNumber(row.items),
          })),
        ),
      onError: onError("items"),
    },
    {
      signal,
      tables: ["ps_crud", "attachments", "attachment_failures"],
      throttleMs: WATCH_THROTTLE_MS,
    },
  )

  db.watch(
    FAILURES_BY_PROJECT_SQL,
    [],
    {
      onResult: (result) =>
        monitor.setFailures(
          toFailureGroups(rowsOf<FailuresByProjectRow>(result)),
        ),
      onError: onError("sync_failures"),
    },
    {
      signal,
      tables: ["sync_failures", "attachments", "attachment_failures"],
      throttleMs: WATCH_THROTTLE_MS,
    },
  )

  // A completion re-reads the failures rather than trusting the watch to
  // have delivered by then.
  monitor.setFailureReader(async () =>
    toFailureGroups(
      (await db.getAll(FAILURES_BY_PROJECT_SQL, [])) as FailuresByProjectRow[],
    ),
  )

  monitor.setSignals(getUploadSignals())
  const unsubscribeSignals = subscribeToUploadSignals(() =>
    monitor.setSignals(getUploadSignals()),
  )

  const stopReportingStalls = reportHiddenDeletionStalls(monitor)

  const updateOnline = () => monitor.setOnline(navigator.onLine)
  updateOnline()
  window.addEventListener("online", updateOnline)
  window.addEventListener("offline", updateOnline)

  return () => {
    controller.abort()
    monitor.setFailureReader(null)
    unsubscribeSignals()
    stopReportingStalls()
    window.removeEventListener("online", updateOnline)
    window.removeEventListener("offline", updateOnline)
  }
}
